package org.catharsis.spendtel.feature.presentation.expensereport

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.outlined.Description
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import org.catharsis.spendtel.feature.data.ExpenseReport
import org.catharsis.spendtel.feature.data.SourceDocument
import org.catharsis.spendtel.feature.data.displayName
import org.catharsis.spendtel.feature.presentation.expensereport.reportStatusMessage

@Composable
fun ExpenseReportBody(
    report: ExpenseReport,
    modifier: Modifier = Modifier,
    separatedModules: Boolean = false,
) {
    var statementsExpanded by remember { mutableStateOf(false) }

    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(if (separatedModules) 32.dp else 24.dp),
    ) {
        when {
            report.isProcessing -> {
                StoryContentModule(separatedModules) {
                    ExpenseReportStatusCard(report = report)
                    ExpenseReportProcessingCard(status = report.status)
                }
            }

            report.isFailed -> {
                StoryContentModule(separatedModules) {
                    ExpenseReportStatusCard(report = report)
                    ExpenseReportFailedCard(errorMessage = report.error)
                }
            }

            report.hasConversationalContent -> {
                ExpenseReportStoryContent(
                    report = report,
                    separatedModules = separatedModules,
                )
            }

            report.isComplete -> {
                if (!separatedModules) {
                    ExpenseReportLegacyFallback()
                }
                if (report.transactions.isNotEmpty() || separatedModules) {
                    ExpenseReportStoryContent(
                        report = report,
                        separatedModules = separatedModules,
                    )
                }
            }
        }

        if (report.sourceDocuments.isNotEmpty()) {
            StoryContentModule(separatedModules) {
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { statementsExpanded = !statementsExpanded },
                    shape = RoundedCornerShape(if (separatedModules) 16.dp else 20.dp),
                    colors = CardDefaults.cardColors(containerColor = Color.White),
                    elevation = CardDefaults.cardElevation(
                        defaultElevation = if (separatedModules) 0.dp else 0.dp,
                    ),
                ) {
                    Column(
                        modifier = Modifier.padding(18.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp),
                    ) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Text(
                                text = "Source statements (${report.sourceDocuments.size})",
                                style = MaterialTheme.typography.titleSmall,
                                fontWeight = FontWeight.SemiBold,
                                color = SpendTelReportColors.ink,
                            )
                            Icon(
                                imageVector = if (statementsExpanded) Icons.Filled.ExpandLess else Icons.Filled.ExpandMore,
                                contentDescription = null,
                                tint = SpendTelReportColors.mint,
                            )
                        }
                        AnimatedVisibility(visible = statementsExpanded) {
                            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                report.sourceDocuments.forEach { document ->
                                    ExpenseReportStatementRow(document = document)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun ExpenseReportStatusCard(report: ExpenseReport) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(20.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = "Status",
                    style = MaterialTheme.typography.labelLarge,
                    color = SpendTelReportColors.inkMuted,
                )
                ExpenseReportStatusChip(status = report.status)
            }
            Text(
                text = reportStatusMessage(report.status),
                style = MaterialTheme.typography.bodyMedium,
                color = SpendTelReportColors.ink.copy(alpha = 0.85f),
            )
        }
    }
}

@Composable
fun ExpenseReportStatementRow(document: SourceDocument) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(SpendTelReportColors.canvasDark)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            imageVector = Icons.Outlined.Description,
            contentDescription = null,
            tint = SpendTelReportColors.mint,
            modifier = Modifier.size(20.dp),
        )
        Text(
            text = document.displayName(),
            style = MaterialTheme.typography.bodySmall,
            modifier = Modifier.weight(1f),
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
            color = SpendTelReportColors.ink,
        )
    }
}
